import { CommonModule } from '@angular/common'
import {
  Component,
  inject,
  Output,
  EventEmitter,
  effect,
  computed,
  Input,
} from '@angular/core'

import { Enums, GeneratedContent } from '@cyborg-monorepo/hunter/common'
import { BadgeComponent, IconComponent, TypographyComponent } from '@hyperkit'

import { QueryStatusColorPipe } from '../../../../../../modules/shared/pipes/query-status-color.pipe'
import { NavigationService } from '../../../../services/navigation.service'
import { QueryClickEvent } from '../../../../services/utils/navigation.utils'

@Component({
  selector: 'pivot-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    TypographyComponent,
    BadgeComponent,
    QueryStatusColorPipe,
  ],
  // Prevent any clicks inside from bubbling up
  host: {
    '(click)': '$event.stopPropagation()',
  },
  templateUrl: './pivot-dropdown.component.html',
  styleUrls: ['./pivot-dropdown.component.scss'],
})
export class PivotDropdownComponent {
  @Input() headerLabel!: string
  @Input() status!: Enums.QueryStatusEnum
  @Input() items: GeneratedContent[] = []
  @Input() huntQueryId!: string
  @Input() headerCategory!: string
  @Input() toolId!: string

  @Output() selectPivot = new EventEmitter<QueryClickEvent>()

  public readonly nav = inject(NavigationService)

  /** open state keyed by pivot path */
  private openMap: Record<string, boolean> = {}
  /** selected node per level */
  selections: GeneratedContent[] = []

  /** header is active if top-level query is selected */
  headerActive = computed(
    () =>
      this.nav.selectedQueryId() === this.huntQueryId &&
      this.nav.pivotPath().length === 0,
  )

  constructor() {
    // Re-compute active selections whenever URL params change.
    // Ensure dropdowns are expanded for the currently selected pivot path
    // and when new pivots are added.
    effect(() => {
      const selQuery = this.nav.selectedQueryId()
      const path = this.nav.pivotPath()

      // Automatically open the root dropdown when pivots exist
      if (this.items.length && !this.openMap['']) {
        this.openMap[''] = true
      }

      this.selections = []
      if (selQuery !== this.huntQueryId) return

      let current = this.items
      let prefix = ''
      for (const id of path) {
        const found = current.find((i) => i.id === id)
        if (!found) break
        this.selections.push(found)
        current = found.pivots || []
        if (!this.openMap[prefix]) {
          this.openMap[prefix] = true
        }
        prefix = this.pathKey(prefix, id)
        if (!this.openMap[prefix]) {
          this.openMap[prefix] = true
        }
      }
    })
  }
  toggle(path: string) {
    this.openMap[path] = !this.openMap[path]
  }

  isOpen(path: string): boolean {
    return !!this.openMap[path]
  }

  pathKey(prefix: string, id: string): string {
    return prefix ? `${prefix}/${id}` : id
  }

  onHeaderClick() {
    this.nav.setSelectedQuery(this.toolId, this.huntQueryId, [])
  }

  select(item: GeneratedContent, level: number, prefix: string) {
    // prefix encodes the parent path as "id1/id2"
    const parentIds = prefix ? prefix.split('/') : []

    const newPath = [...parentIds, item.id!]

    this.nav.setSelectedQuery(this.toolId, this.huntQueryId, newPath)
  }
}
